import type { FormatType } from '@/types/attendanceFilter.types';

/**
 * Time validation patterns for different contexts
 */

/**
 * Strict time validation pattern - requires complete valid time in HH:mm:ss format (24-hour)
 * Use this for final validation in schemas and API submissions
 * Examples: "09:00:00", "23:59:59", "00:00:00"
 */
export const TIME_STRICT_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

/**
 * HH:mm time validation pattern (no seconds) — used for form fields in schedule jornada
 * Examples: "09:00", "23:59", "00:00"
 */
export const TIME_HH_MM_REGEX = /^([0-1]\d|2[0-3]):[0-5]\d$/;

/**
 * Permissive time validation pattern - allows partial input during typing
 * Use this for real-time input validation in forms to enable incremental user input
 * Allows patterns like: "0", "09", "09:", "09:3", "09:30", "09:30:", "09:30:0", "09:30:00"
 * Note: This intentionally allows incomplete times for better UX during typing
 */
export const TIME_INPUT_REGEX = /^([0-1]?[0-9]|2[0-3])?:?([0-5]?[0-9])?:?([0-5]?[0-9])?$/;

/**
 * Validates if a time string is a complete valid time in HH:mm:ss format
 * @param time - Time string to validate
 * @returns true if the time is valid and complete
 * @example
 * isValidCompleteTime("09:00:00") // returns true
 * isValidCompleteTime("09:00") // returns false
 * isValidCompleteTime("25:00:00") // returns false
 */
export function isValidCompleteTime(time: string): boolean {
  return TIME_STRICT_REGEX.test(time);
}

/**
 * Validates if a time string is acceptable as partial input (during typing)
 * @param time - Time string to validate
 * @returns true if the time is valid partial or complete input
 * @example
 * isValidPartialTime("09") // returns true
 * isValidPartialTime("09:30") // returns true
 * isValidPartialTime("25:00:00") // returns false
 */
export function isValidPartialTime(time: string): boolean {
  return TIME_INPUT_REGEX.test(time) || time === '';
}

/**
 * Converts time in HH:MM:SS format to decimal hours
 * @param timeString - Time string in HH:MM:SS format (e.g., "08:30:00")
 * @returns Decimal hours (e.g., 8.5) or null if invalid format
 * @example
 * convertTimeToDecimal("08:30:00") // returns 8.5
 * convertTimeToDecimal("01:15:30") // returns 1.258333...
 */
export function convertTimeToDecimal(timeString: string): number | null {
  if (!timeString || typeof timeString !== 'string') return null;
  
  const timeParts = timeString.split(':');
  if (timeParts.length !== 3) return null;
  
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);
  const seconds = parseInt(timeParts[2], 10);
  
  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
  if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) return null;
  
  return hours + (minutes / 60) + (seconds / 3600);
}

/**
 * Converts decimal hours to time in HH:MM:SS format
 * @param decimalHours - Decimal hours (e.g., 8.5)
 * @returns Time string in HH:MM:SS format (e.g., "08:30:00") or null if invalid
 * @example
 * convertDecimalToTime(8.5) // returns "08:30:00"
 * convertDecimalToTime(1.258333) // returns "01:15:30"
 */
export function convertDecimalToTime(decimalHours: number): string | null {
  if (typeof decimalHours !== 'number' || isNaN(decimalHours) || decimalHours < 0) return null;
  
  const hours = Math.floor(decimalHours);
  const remainingMinutes = (decimalHours - hours) * 60;
  const minutes = Math.floor(remainingMinutes);
  let seconds = Math.round((remainingMinutes - minutes) * 60);
  
  // Handle seconds overflow
  let finalMinutes = minutes;
  let finalHours = hours;
  if (seconds >= 60) {
    seconds = 0;
    finalMinutes += 1;
    if (finalMinutes >= 60) {
      finalMinutes = 0;
      finalHours += 1;
    }
  }
  
  const hoursStr = String(finalHours).padStart(2, '0');
  const minutesStr = String(finalMinutes).padStart(2, '0');
  const secondsStr = String(seconds).padStart(2, '0');
  
  return `${hoursStr}:${minutesStr}:${secondsStr}`;
}

/**
 * Extracts time in HH:mm:ss format from various time formats
 * @param timeValue - Time string in various formats (HH:mm, HH:mm:ss, ISO 8601, etc.)
 * @returns Time string in HH:mm:ss format, or null if invalid/cannot extract
 * @example
 * extractTimeInHhMmSs("09:30") // returns "09:30:00"
 * extractTimeInHhMmSs("2025-09-16T07:15:00") // returns "07:15:00"
 * extractTimeInHhMmSs("09:30:00") // returns "09:30:00"
 * extractTimeInHhMmSs(null) // returns null
 */
export function extractTimeInHhMmSs(timeValue: string | null | undefined): string | null {
  if (!timeValue) return null;

  const trimmedValue = timeValue.trim();

  // If it's already in HH:mm:ss format, return as is
  if (TIME_STRICT_REGEX.test(trimmedValue)) {
    return trimmedValue;
  }

  // If it's HH:mm format, append :00
  if (/^\d{2}:\d{2}$/.test(trimmedValue)) {
    return `${trimmedValue}:00`;
  }

  // If it's ISO 8601 format (contains 'T'), extract the time part
  if (trimmedValue.includes('T')) {
    const timePart = trimmedValue.split('T')[1];
    if (timePart) {
      // Extract HH:mm:ss (first 8 characters)
      const extractedTime = timePart.substring(0, 8);
      if (TIME_STRICT_REGEX.test(extractedTime)) {
        return extractedTime;
      }
    }
  }

  return null;
}

/**
 * Converts and formats time value between HH:MM:SS and decimal formats
 * @param timeValue - Time value in HH:MM:SS format or decimal
 * @param formatType - Target format type: 'HH:MM:SS' or 'DECIMAL'
 * @param currentFormat - Current format of the timeValue (optional, auto-detects if not provided)
 * @returns Formatted time string or original value if conversion fails
 * @example
 * convertTimeFormat("08:30:00", "DECIMAL") // returns "8,5000"
 * convertTimeFormat(8.5, "HH:MM:SS") // returns "08:30:00"
 * convertTimeFormat("01:00:00", "DECIMAL") // returns "1,0000"
 */
export function convertTimeFormat(
  timeValue: string | number,
  formatType: FormatType,
  currentFormat?: FormatType,
): string {
  if (!timeValue && timeValue !== 0) return '';
  
  // Auto-detect current format if not provided
  const isCurrentlyTimeString = typeof timeValue === 'string' && timeValue.includes(':');
  const detectedCurrentFormat = currentFormat ?? (isCurrentlyTimeString ? 'HH:MM:SS' : 'DECIMAL');
  
  // Already in target format
  if (detectedCurrentFormat === formatType) {
    if (typeof timeValue === 'number') {
      return timeValue.toFixed(4).replace('.', ',');
    }
    return timeValue;
  }
  
  // Convert from HH:MM:SS to DECIMAL
  if (detectedCurrentFormat === 'HH:MM:SS' && formatType === 'DECIMAL') {
    const decimal = convertTimeToDecimal(String(timeValue));
    return decimal !== null ? decimal.toFixed(4).replace('.', ',') : String(timeValue);
  }
  
  // Convert from DECIMAL to HH:MM:SS
  if (detectedCurrentFormat === 'DECIMAL' && formatType === 'HH:MM:SS') {
    const numericValue = typeof timeValue === 'string' ? parseFloat(timeValue) : timeValue;
    const timeString = convertDecimalToTime(numericValue);
    return timeString ?? String(timeValue);
  }
  
  return String(timeValue);
}

/**
 * Strips seconds from HH:mm:ss → HH:mm for display in form TimePickers
 */
export function toHHmm(time: string): string {
  if (time.length < 5) return '00:00';
  return time.substring(0, 5);
}

/**
 * Appends seconds to HH:mm → HH:mm:ss for API submission
 */
export function appendSeconds(time: string): string {
  if (time.length < 5) return '00:00:00';
  return `${time}:00`;
}

/**
 * Normalizes a time string from the UI form to HH:mm:ss format for API submission.
 * Returns null if the value is empty or whitespace-only.
 * Pads HH:mm to HH:mm:ss by appending :00 if seconds are not provided.
 * @example
 * normalizeHoursForBackend('02:30')    // returns '02:30:00'
 * normalizeHoursForBackend('02:30:00') // returns '02:30:00'
 * normalizeHoursForBackend('')         // returns null
 */
export function normalizeHoursForBackend(hours: string): string | null {
  if (!hours || hours.trim() === '') return null;
  const parts = hours.split(':');
  if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
  return hours;
}
